import { di } from 'react-magnetic-di';

import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';

interface Space {
	spaceId: string;
	name: string;
	description: string;
	default: boolean;
}

enum WorkspacePermission {
	manageAutomation = 'workspace/manage-automation',
	manageApp = 'workspace/manage-app',
}

interface OrgContextApi {
	orgId: string;
	workspaceId: string;
	defaultSpace: Space;
	isWorkspaceAdmin: boolean;
	siteCloudIds: string[];
	collaborationContextId?: string;
	permissions: WorkspacePermission[];
}

type Permissions = Record<keyof typeof WorkspacePermission, boolean>;

const fetchOrgContextData = async (workspaceId: string): Promise<OrgContextApi> => {
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/workspace/context`,
	);
	if (!response.ok) {
		throw new Error('Failed to fetch studio workspace context data');
	}
	return response.json();
};

export const useLegacyWorkspacePermissions = (
	workspaceId: string,
): {
	permissions: Permissions;
	loading: boolean;
} => {
	di(useQuery);

	const { data, isFetching: loading } = useQuery({
		queryKey: ['workspacePermissions', workspaceId],
		queryFn: () => fetchOrgContextData(workspaceId),
		enabled: !!workspaceId,
	});

	const permissions: Permissions = {
		manageAutomation: data?.permissions.includes(WorkspacePermission.manageAutomation) ?? false,
		manageApp: data?.permissions.includes(WorkspacePermission.manageApp) ?? false,
	};

	return {
		permissions,
		loading,
	};
};
